import { isEqual } from 'es-toolkit'
import { useState, useEffect, useMemo, useCallback } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'

import type { EditTemplateProps } from 'api/template/types'

import { updateTemplateData, getTemplateData, getTemplateList, selectTemplateStatus } from 'slices/templateSlice'
import { selectWorkspacesStatus, getWorkspace } from 'slices/workspacesSlice'

import ListDetailView from 'components/common/ListDetailView/ListDetailVIew'
import { TIME_INTERVAL } from 'components/common/constants'

import useAuthority from 'hooks/useAuthority'
import useBusinessTime from 'hooks/useBusinessTime'

import TemplateDelete from './TemplateDelete'
import { TemplateDetailView } from './TemplateDetailView'

const IDS_NUM = 288 // 24時間 * 60分 / 5分 = 288
const initEditTemplateData = {
  id: null,
  ids: Array(IDS_NUM).fill(null),
  name: '',
}
// templateIDが1から始まるため､新規追加アイテムのIDは0とする
const NEW_TEMPLATE_ID = 0
const MAX_ITEMS = 100

const handleDetailClick = () => window.open('https://help.smileboard.jp/use_templates', '_blank')

const TemplateEdit = () => {
  const params = useParams<'workspaceId'>()
  const workspaceId = useMemo(() => Number(params.workspaceId), [params.workspaceId])

  const [templateNameValidity, setTemplateNameValidity] = useState(false)
  const [editData, setEditData] = useState<EditTemplateProps>(initEditTemplateData)
  const [initEditData, setInitEditData] = useState<EditTemplateProps>(initEditTemplateData)
  const [selectedTemplateId, setSelectedTemplateId] = useState<number>()
  const [openDelete, setOpenDelete] = useState(false)

  const dispatch = useDispatch()

  const { workspace } = useSelector(selectWorkspacesStatus, shallowEqual)
  const { templateList, template, templateWorkspaceId, isRequesting, errorMessage } = useSelector(
    selectTemplateStatus,
    shallowEqual
  )

  const { isReadOnlyWorkspace } = useAuthority(workspaceId)
  const { businessStartIndex } = useBusinessTime({ interval: TIME_INTERVAL.FIVE })

  useEffect(() => {
    dispatch(getWorkspace(workspaceId))
  }, [dispatch, workspaceId])

  useEffect(() => {
    if (!template) {
      return
    }
    // idsの配列をbusinessStartIndexの位置で分割して順序を入れ替える
    const beforeStartIndexIds = template.ids.slice(0, businessStartIndex)
    const afterStartIndexIds = template.ids.slice(businessStartIndex)
    const ids = afterStartIndexIds.concat(beforeStartIndexIds)

    const newEditData = { ...template, ids: ids }

    setInitEditData(newEditData)
    setEditData(newEditData)
  }, [businessStartIndex, template])

  const listItems = useMemo(
    () =>
      // 前WSのtemplateListが残っているため、workspaceIdとtemplateWorkspaceIdが不一致の間はsetScheduleTypeListItemsしない
      workspaceId === templateWorkspaceId
        ? templateList.map(({ id, name }) => ({
            id,
            title: name,
            data: `ID: ${id}`,
          }))
        : [],
    [templateList, templateWorkspaceId, workspaceId]
  )

  const newListItem = useMemo(() => ({ title: '未保存の項目', id: NEW_TEMPLATE_ID, data: 'ID: 未保存' }), [])

  const disabled = useMemo(() => !(editData && templateNameValidity), [editData, templateNameValidity])

  const unchanged = useMemo(
    () => editData.name === initEditData.name && isEqual(editData.ids, initEditData.ids),
    [editData.ids, editData.name, initEditData.ids, initEditData.name]
  )

  const handleSubmit = useCallback(() => {
    // idsの配列をIDS_NUM - businessStartIndexの位置で分割して順序を入れ替える
    const beforeStartIndexIds = editData.ids.slice(0, IDS_NUM - businessStartIndex)
    const afterStartIndexIds = editData.ids.slice(IDS_NUM - businessStartIndex)
    const ids = afterStartIndexIds.concat(beforeStartIndexIds)

    const id = editData.id === NEW_TEMPLATE_ID ? null : editData.id
    const schedules = ids.map(i => (i === null || i.isSupport ? null : i.id))
    const workspaces = ids.map(i => (i === null ? null : i.isSupport ? i.id : workspaceId))
    dispatch(
      updateTemplateData(workspaceId, {
        schedules,
        workspaces,
        name: editData.name,
        id,
      })
    )
  }, [businessStartIndex, dispatch, editData.id, editData.ids, editData.name, workspaceId])

  const handleCancel = useCallback(() => template && setEditData(initEditData), [initEditData, template])

  const handleAddNewItem = useCallback(() => {
    setEditData({
      ...initEditTemplateData,
      id: NEW_TEMPLATE_ID,
    })
  }, [])

  const listFunc = useCallback(
    (callback: (newList: { id: number; name: string }[]) => void) => getTemplateList(workspaceId, callback),
    [workspaceId]
  )

  const detailFunc = useCallback((id: number) => getTemplateData(workspaceId, id), [workspaceId])

  const deleteDialog = useCallback(
    (onSuccess: () => void) => (
      <TemplateDelete
        isOpen={openDelete}
        workspaceId={workspaceId}
        templateId={selectedTemplateId}
        templateName={editData.name}
        onSuccess={() => {
          onSuccess()
          setOpenDelete(false)
        }}
        onCancel={() => setOpenDelete(false)}
      />
    ),
    [editData.name, openDelete, selectedTemplateId, workspaceId]
  )

  const handleSelectItem = useCallback(
    (id: number) => dispatch(getTemplateData(workspaceId, id)),
    [dispatch, workspaceId]
  )

  const handleChange = useCallback(<K extends keyof EditTemplateProps>(key: K, value: EditTemplateProps[K]) => {
    setEditData(prev => ({ ...prev, [key]: value }))
  }, [])

  const handleValidate = useCallback((value: boolean) => {
    setTemplateNameValidity(value)
  }, [])

  const handleDeleteButtonClick = useCallback(() => {
    setOpenDelete(true)
  }, [])

  return (
    <>
      <ListDetailView
        title="予定テンプレート管理"
        badgeLabel={workspace?.name}
        addButton={{ onClick: handleAddNewItem, label: '予定テンプレートの追加' }}
        maxItems={MAX_ITEMS}
        listItems={listItems}
        onSelectItem={handleSelectItem}
        newListItem={newListItem}
        emptyList={{
          title: '予定テンプレートがまだ登録されていません',
          text: ' 作業計画の予定テンプレートを作成できます。予定テンプレートは個人の1日の予定単位で作成が可能です。作業計画画面で複数人選択して、一括で予定テンプレートを入力する事も可能です。',
          buttonLabel: '予定テンプレートについてもっと詳しく',
          onClick: handleDetailClick,
        }}
        emptyDetail={{
          title: '予定テンプレートが選択されていません',
          text: '予定テンプレートを選択して、詳細情報を編集しましょう。',
        }}
        footer={{
          onCancel: handleCancel,
          onSubmit: handleSubmit,
          updatedAt: template?.updatedAt,
          updatedBy: template?.updatedByName,
          cancelDisabled: unchanged,
          submitDisabled: disabled || unchanged || isReadOnlyWorkspace,
        }}
        isRequesting={isRequesting}
        errorMessage={errorMessage}
        selectedId={selectedTemplateId}
        setSelectedId={setSelectedTemplateId}
        newItemId={NEW_TEMPLATE_ID}
        getListFunc={listFunc}
        getDetailFunc={detailFunc}
        deleteDialog={deleteDialog}
        storeId={template?.id}
      >
        <TemplateDetailView
          editData={editData}
          onChange={handleChange}
          onValidate={handleValidate}
          onDeleteButtonClick={handleDeleteButtonClick}
          disabledDeleteButton={selectedTemplateId === NEW_TEMPLATE_ID || isReadOnlyWorkspace}
        ></TemplateDetailView>
      </ListDetailView>
    </>
  )
}

export default TemplateEdit
